<template>
  <div>
    <BaseModal v-model="showModal" size="lg">
      <StatementDocumentViewer :pdfUrl="pdfUrl" :modelValue="documentReadConfirmed"
        @update:modelValue="documentReadConfirmed = $event" />
    </BaseModal>
    <b-form @submit.prevent="onSubmit">
      <AlertDismiss ref="alert" />
      <div v-if="noTemplates">
        <div class="card">
          <div class="card-body text-center">
            Aún no se han creado plantillas de declaración.
          </div>
        </div>
      </div>

      <b-overlay v-else :show="isfetching" variant="white">
        <div class="mb-3">
          <label>Formulario de declaración:</label>
          <select class="form-control" v-model="selectedTemplate">
            <option disabled :value="null">-- Seleccione un formulario --</option>
            <option v-for="template in templates" :key="template.id" :value="template">
              {{ template.name }}
            </option>
          </select>
        </div>
      </b-overlay>
      <div v-if="selectedTemplate" class="mt-3">
        <hr>

        <transition name="fade">
          <div v-if="isQuestionDataReady">
            <StatementQuestion
              v-for="(question, index) in selectedTemplate.questions"
              :key="question.id"
              :question="question"
              :index="index"
              :modelValue="answers[index]"
              :path="[index]"
              :disabled="isUploading"
              @update:modelValue="handleAnswerUpdate"
              />
          </div>
        </transition>

        <div class="mt-3">
          <div class="mb-3">
            <div v-if="!documentReadConfirmed" class="d-flex align-items-center">
              <b-icon
                icon="x-circle-fill"
                variant="danger"
                class="me-1"
              ></b-icon>
              <small class="text-muted">Debe confirmar haber leído el documento antes de enviar.</small>
            </div>
            <div v-if="documentReadConfirmed" class="d-flex align-items-center">
              <b-icon
                icon="check-circle-fill"
                variant="success"
                class="me-1"
              ></b-icon>
              <small class="text-success">Documento leído y confirmado.</small>
            </div>
          </div>
          
          <div class="d-flex align-items-center gap-3">
            <button class="btn btn-outline-primary" @click="openDocumentModal" type="button">
              <b-icon icon="file-earmark-pdf" class="mr-1" />
              <span class="ms-2">Ver documento</span>
            </button>
            
            <button class="btn btn-primary" type="submit" :disabled="isUploading || !documentReadConfirmed">
              <b-spinner v-if="isUploading" small />
              Enviar
            </button>
          </div>
        </div>

      </div>

    </b-form>
  </div>
</template>

<script>
import {mapActions} from 'vuex';
import {EventNames} from '@/plugins/enums';
import AlertDismiss from "@/components/shared/Alert-dismiss.vue";
import BaseModal from '@/components/shared/Base-modal.vue';
import StatementDocumentViewer from '@/components/shared/StatementDocumentViewer.vue';
import StatementQuestion
  from '@/components/FamilyStatements/FamilyStatementMenu/FamilyStatementCreate/Family-statement-question.vue';

export default {
  components: {
    AlertDismiss,
    BaseModal,
    StatementDocumentViewer,
    StatementQuestion,
  },

  data() {
    return {
      isfetching: true,
      showModal: false,
      agreed: false,
      pdfUrl: "",
      selectedTemplate: null,
      templates: [],
      answers: [],
      isUploading: false,
      documentReadConfirmed: false,
    };
  },

  computed: {
    noTemplates() {
      return !this.isfetching && this.templates.length === 0;
    },
    isQuestionDataReady() {
      return (
        this.selectedTemplate?.questions &&
        this.answers.length === this.selectedTemplate.questions.length
      );
    }
  },

  async mounted() {
    this.isfetching = true;
    await this.getTemplates();
    this.isfetching = false;
  },

  methods: {
    ...mapActions([
      "getFamilyDocumentUrl",
      "getAllFamilyStatementTemplatesForUser",
      "saveFamilyStatement",
    ]),

    async getTemplates() {
      this.isfetching = true;
      const result = await this.getAllFamilyStatementTemplatesForUser();

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        this.isfetching = false;
        return;
      }

      this.templates = result.data;
      this.isfetching = false;
    },

    async openDocumentModal() {
      this.showModal = true;
      const result = await this.getFamilyDocumentUrl({ route: this.selectedTemplate.document.route });

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        this.showModal = false;
        return;
      }

      this.pdfUrl = result.data;
    },

    buildAnswerStructure(questions) {
      return questions.map(question => {
        return {
          questionId: question.id,
          isTrue: question.type === 'societyList' ? true : null,
          value: "",
          details: []
        };
      });
    },

    resetAnswers() {
      this.documentReadConfirmed = false;
      if (this.selectedTemplate?.questions) {
        this.answers = this.buildAnswerStructure(this.selectedTemplate.questions);
      } else {
        this.answers = [];
      }
    },

    handleAnswerUpdate(event) {
      const { path, value: newValue } = event;
      if (path.length === 1) {
        const mainIndex = path[0];
        this.answers.splice(mainIndex, 1, newValue);
      } else {
        // This case should ideally not be reached if paths are always [index]
        console.error("Error updating answer: Path length is not 1", path, newValue);
      }
    },

    async onSubmit() {
      this.isUploading = true;
      const questionFamilies = [];

      for (const [index, answer] of this.answers.entries()) {
        const question = this.selectedTemplate.questions[index];

        const payload = {
          questionId: answer.questionId,
        };

        if (question.type !== 'SocietyList') {
          payload.isCorrect = answer.isTrue;
        }

        if (answer.isTrue) {
          payload.societieRequests = answer.details;
        }
        questionFamilies.push(payload);
      }

      const payload = {
        templatePepId: this.selectedTemplate.id,
        questionPeps: questionFamilies,
      };

      const result = await this.saveFamilyStatement(payload);

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        this.isUploading = false;
        return;
      }

      this.$refs.alert.showAlert("Declaración enviada", "success");
      this.$eventBus.$emit(EventNames.CreatedFamilyStatement);
      this.isUploading = false;
      this.resetAnswers();
    },
  },

  watch: {
    selectedTemplate() {
      this.resetAnswers();
    }
  }
};
</script>
